import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronRight,
  EllipsisVertical,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from 'recharts'
import { Button } from '@/components/ui/button'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { CellNumber } from '@/components/custom/datatable/cells/cell-number'
import { ContactDeleteDialog } from '@/components/custom/contacts/contact-delete-dialog'
import {
  buildScorecardStartValue,
  buildScorecardChartData,
  formatScorecardValue,
  formatScorecardGoal,
  formatScorecardGoalPercentage,
  getScorecardDirectionLabel,
  getScorecardGoalChangeLabel,
  formatScorecardFiscalYearLabel,
  shouldShowAnnualScorecardView,
  type ScorecardChartDatum,
  type ScorecardChartRange,
  type ScorecardGoalProgress,
  type ScorecardSeriesPoint,
} from '@/features/scorecard/scorecard-table.utils'
import {
  formatScorecardStatusLabel,
  scorecardStatusOptionsFor,
} from '@/features/scorecard/scorecard-status'
import {
  useExpandedMetricSeries,
  type ExpandedPeriodType,
} from '@/hooks/use-metrics'
import { usePortalExpandedMetricSeries } from '@/hooks/use-portal-metrics'
import { formatDate, formatPercentage } from '@/lib/format'
import { formatEnumValue } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import type { CompanyMetricsView } from '@/services/metrics.service'
import type {
  ScorecardKpiDto,
  ScorecardKpiStatus,
  ScorecardMetricDataType,
  ScorecardPeriodType,
  ScorecardTrackedKpiDto,
} from '@/services/scorecard.service'

const HEADER_BG =
  'color-mix(in srgb, var(--primary) 7%, var(--background))' as const
const SCORECARD_TABLE_MIN_WIDTH = 820
const SCORECARD_TABLE_EDIT_MIN_WIDTH = 880
const NAVY = '#1b3a5b'
const CORAL = '#c0654a'
const GRID_STROKE = 'color-mix(in srgb, var(--primary) 10%, transparent)'

const chartConfig = {
  actual: { label: 'Actual', color: NAVY },
  target: { label: 'Goal path', color: CORAL },
} satisfies ChartConfig

const CHART_VIEW_OPTIONS: { value: CompanyMetricsView; label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUAL', label: 'Annual' },
]

const TRACKED_KPI_PERIOD_OPTIONS: ScorecardPeriodType[] = [
  'MONTHLY',
  'QUARTERLY',
  'LTM',
  'AS_OF',
]

const CHART_RANGE_OPTIONS: {
  value: ScorecardChartRange
  label: string
}[] = [
  { value: 'GOAL_PERIOD', label: 'Goal period' },
  { value: 'FULL_HISTORY', label: 'Full history' },
]

type ScorecardSurface = 'internal' | 'portal'

function getKpiName(kpi: ScorecardKpiDto) {
  const displayName = kpi.displayName?.trim()
  if (displayName) return displayName
  if (kpi.actualSource === 'MANUAL' || kpi.actualEditable)
    return kpi.metricLabel.labeledAs

  const action = getScorecardDirectionLabel(kpi)
  return `${action} ${kpi.metricLabel.labeledAs}`
}

function getTrackedKpiName(kpi: ScorecardTrackedKpiDto) {
  return kpi.displayName?.trim() || kpi.metricLabel.labeledAs
}

function getKpiDataType(
  kpi: ScorecardKpiDto
): ScorecardMetricDataType | undefined {
  return (
    kpi.metricLabel.dataType ??
    kpi.metricLabel.metricType?.dataType ??
    undefined
  )
}

function getTrackedKpiDataType(
  kpi: ScorecardTrackedKpiDto
): ScorecardMetricDataType | undefined {
  return (
    kpi.metricLabel.dataType ??
    kpi.metricLabel.metricType?.dataType ??
    undefined
  )
}

function formatForDataType(dataType: ScorecardMetricDataType | undefined) {
  if (dataType === 'CURRENCY') return 'currency'
  if (dataType === 'PERCENTAGE') return 'percentage'
  return undefined
}

function formatPeriodLabel(periodType: ScorecardPeriodType) {
  if (periodType === 'LTM') return 'LTM'
  if (periodType === 'TTM') return 'TTM'
  if (periodType === 'YTD') return 'YTD'
  if (periodType === 'AS_OF') return 'As of'
  return formatEnumValue(periodType)
}

function formatValue(
  value: number | null | undefined,
  dataType: ScorecardMetricDataType | undefined,
  unitLabel?: string | null
) {
  return formatScorecardValue(value, dataType, unitLabel)
}

function buildTrackedSparklineData(points: ScorecardSeriesPoint[]) {
  return points.map((point) => ({
    periodEndDate: point.periodEndDate,
    label: point.quarter,
    axisLabel: point.quarter,
    actual: point.value,
    target: null,
    isCheckpoint: false,
    checkpointLabel: null,
  }))
}

function formatAsOfDate(value: string | Date | null | undefined) {
  if (!value) return null

  try {
    const date = typeof value === 'string' ? new Date(value) : value
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date)
  } catch {
    return null
  }
}

function formatGoalWindow(
  kpi: Pick<ScorecardKpiDto, 'startDate' | 'targetDate'>
) {
  return `${formatDate(kpi.startDate)} → ${formatDate(kpi.targetDate)}`
}

function formatTargetDueLabel(
  kpi: Pick<ScorecardKpiDto, 'periodType' | 'startDate' | 'targetDate'>,
  fiscalYearEnd?: string | null
) {
  if (kpi.periodType === 'ANNUAL') {
    const startFiscalYear = formatScorecardFiscalYearLabel(
      kpi.startDate,
      fiscalYearEnd
    )
    const targetFiscalYear = formatScorecardFiscalYearLabel(
      kpi.targetDate,
      fiscalYearEnd
    )

    if (startFiscalYear && targetFiscalYear) {
      return startFiscalYear === targetFiscalYear
        ? targetFiscalYear
        : `${startFiscalYear} → ${targetFiscalYear}`
    }
  }

  return `By ${formatDate(kpi.targetDate)}`
}

function formatCheckpointSummary(kpi: Pick<ScorecardKpiDto, 'checkpoints'>) {
  if (kpi.checkpoints.length === 0) return 'Straight-line path'
  if (kpi.checkpoints.length === 1) return '1 checkpoint'
  return `${kpi.checkpoints.length} checkpoints`
}

function scorecardPeriodToChartPeriod(
  periodType: ScorecardPeriodType
): ExpandedPeriodType {
  if (periodType === 'MONTHLY') return 'MONTHLY'
  if (periodType === 'QUARTERLY') return 'QUARTERLY'
  if (periodType === 'AS_OF') return 'QUARTERLY'
  if (periodType === 'YTD') return 'YTD'
  return 'LTM'
}

function scorecardPeriodToInitialChartView(
  periodType: ScorecardPeriodType
): CompanyMetricsView {
  if (periodType === 'MONTHLY') return 'MONTHLY'
  if (periodType === 'LTM') return 'MONTHLY'
  if (periodType === 'QUARTERLY' || periodType === 'AS_OF') return 'QUARTERLY'
  return 'ANNUAL'
}

function getChartViewOptions(
  kpi: Pick<ScorecardKpiDto, 'periodType' | 'startDate' | 'targetDate'>
) {
  if (shouldShowAnnualScorecardView(kpi)) {
    return CHART_VIEW_OPTIONS
  }

  return CHART_VIEW_OPTIONS.filter((option) => option.value !== 'ANNUAL')
}

function getTrackedKpiTrendPeriod(
  availablePeriods: ScorecardPeriodType[]
): ScorecardPeriodType | null {
  for (const periodType of TRACKED_KPI_PERIOD_OPTIONS) {
    if (availablePeriods.includes(periodType)) {
      return periodType
    }
  }

  return availablePeriods[0] ?? null
}

function useScorecardSeries({
  surface,
  companyId,
  metricLabelId,
  periodType,
  view,
  enabled,
}: {
  surface: ScorecardSurface
  companyId?: string
  metricLabelId: string
  periodType: ExpandedPeriodType
  view?: CompanyMetricsView
  enabled: boolean
}) {
  const internal = useExpandedMetricSeries(
    companyId ?? '',
    metricLabelId,
    periodType,
    enabled && surface === 'internal' && Boolean(companyId),
    view
  )
  const portal = usePortalExpandedMetricSeries(
    metricLabelId,
    periodType,
    enabled && surface === 'portal',
    view
  )

  return surface === 'portal' ? portal : internal
}

function getStatusBadgeClass(status: ScorecardKpiDto['status']) {
  const base =
    'inline-flex h-5 items-center rounded-full border px-2 py-0 text-[11px] font-normal leading-5 transition-colors'

  if (status === 'ACHIEVED' || status === 'ON_TRACK') {
    return cn(base, 'border-[#08701180] bg-[#0870110D] text-[#087011]')
  }

  if (status === 'OFF_TRACK') {
    return cn(base, 'border-[#DF001680] bg-[#DF00160D] text-[#DF0016]')
  }

  if (status === 'AT_RISK') {
    return cn(base, 'border-amber-500/50 bg-amber-500/10 text-amber-700')
  }

  return cn(base, 'border-primary/20 bg-white text-primary/70')
}

function StatusBadge({
  status,
  className,
  showChevron = false,
  isOverridden = false,
}: {
  status: ScorecardKpiDto['status']
  className?: string
  showChevron?: boolean
  isOverridden?: boolean
}) {
  return (
    <span
      className={cn(
        getStatusBadgeClass(status),
        showChevron && 'gap-0.5 pr-1',
        className
      )}
    >
      {formatScorecardStatusLabel(status)}
      {isOverridden ? (
        <span
          role="img"
          aria-label="Manually set"
          className="ml-1 text-[10px] leading-none opacity-70"
          title="Manually set"
        >
          •
        </span>
      ) : null}
      {showChevron ? (
        <ChevronDown className="size-2.5 shrink-0 text-current opacity-55" />
      ) : null}
    </span>
  )
}

function StatusCell({
  kpi,
  canEdit,
  isUpdating,
  onStatusChange,
}: {
  kpi: ScorecardKpiDto
  canEdit: boolean
  isUpdating: boolean
  onStatusChange?: (
    kpi: ScorecardKpiDto,
    status: ScorecardKpiStatus | 'CALCULATED'
  ) => Promise<void>
}) {
  const isOverridden =
    kpi.statusSource === 'MANUAL' && kpi.status !== kpi.calculatedStatus

  if (!canEdit || kpi.configurationEditable === false || !onStatusChange) {
    return (
      <TableCell className="py-4">
        <StatusBadge status={kpi.status} isOverridden={isOverridden} />
      </TableCell>
    )
  }

  return (
    <TableCell
      className="cursor-default py-4"
      onClick={(event) => event.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={isUpdating}
            className="inline-flex cursor-pointer items-center gap-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={`Change status for ${getKpiName(kpi)}`}
          >
            <StatusBadge
              status={kpi.status}
              showChevron
              isOverridden={isOverridden}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem
            disabled={isUpdating || kpi.statusSource === 'CALCULATED'}
            onClick={() => {
              void onStatusChange(kpi, 'CALCULATED')
            }}
          >
            <Check
              className={cn(
                'size-4',
                kpi.statusSource === 'CALCULATED' ? 'opacity-100' : 'opacity-0'
              )}
            />
            Use automatic status
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {scorecardStatusOptionsFor(kpi.status).map((status) => {
            const isCurrent = status === kpi.status
            return (
              <DropdownMenuItem
                key={status}
                disabled={
                  isUpdating || (isCurrent && kpi.statusSource === 'MANUAL')
                }
                onClick={() => {
                  void onStatusChange(kpi, status)
                }}
              >
                <Check
                  className={cn(
                    'size-4',
                    kpi.statusSource === 'MANUAL' && isCurrent
                      ? 'opacity-100'
                      : 'opacity-0'
                  )}
                />
                {formatScorecardStatusLabel(status)}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  )
}

function StartValueCell({
  startValue,
  dataType,
  unitLabel,
  isLoading,
}: {
  startValue: number | null
  dataType: ScorecardMetricDataType | undefined
  unitLabel?: string | null
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <TableCell className="py-4">
        <Skeleton className="h-7 w-24" />
      </TableCell>
    )
  }

  if (startValue === null) {
    return (
      <TableCell className="py-4">
        <span className="text-sm text-primary/70">—</span>
      </TableCell>
    )
  }

  return (
    <TableCell className="py-4">
      <span className="text-sm font-medium tabular-nums text-primary">
        {formatValue(startValue, dataType, unitLabel)}
      </span>
    </TableCell>
  )
}

function GoalProgressCell({
  kpi,
  progress,
  dataType,
  unitLabel,
  isLoading,
}: {
  kpi: ScorecardKpiDto
  progress: ScorecardGoalProgress | null
  dataType: ScorecardMetricDataType | undefined
  unitLabel?: string | null
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <TableCell className="py-4">
        <Skeleton className="h-7 w-24" />
      </TableCell>
    )
  }

  if (!progress || !Number.isFinite(progress.percent)) {
    return (
      <TableCell className="py-4">
        <span className="text-sm text-primary/70">—</span>
      </TableCell>
    )
  }

  const percent = Math.max(0, progress.percent)
  const hasNoProgressYet =
    progress.progressValue <= 0 && progress.remainingValue > 0
  const remainingLabel =
    progress.remainingValue <= 0
      ? 'Goal met'
      : `Remaining ${formatValue(progress.remainingValue, dataType, unitLabel)}`
  const progressSubLabel = hasNoProgressYet
    ? `No progress yet · ${remainingLabel}`
    : remainingLabel

  return (
    <TableCell className="py-4">
      <div className="flex min-w-0 flex-col items-start gap-1">
        <span className="text-sm font-medium tabular-nums text-primary">
          {formatPercentage(percent)}
        </span>
        <BulletBar kpi={kpi} goalProgress={progress} className="w-32" />
        <span
          className={cn(
            'max-w-full truncate text-[11px] tabular-nums',
            progress.remainingValue <= 0
              ? 'text-emerald-700'
              : 'text-primary/70'
          )}
          title={progressSubLabel}
        >
          {progressSubLabel}
        </span>
      </div>
    </TableCell>
  )
}

function ActualValueCell({
  kpi,
  dataType,
  unitLabel,
}: {
  kpi: ScorecardKpiDto
  dataType: ScorecardMetricDataType | undefined
  unitLabel?: string | null
}) {
  const [open, setOpen] = useState(false)
  const actual = kpi.actual

  if (!actual) {
    return (
      <TableCell className="py-4">
        <span className="text-sm text-primary/70">No actual yet</span>
      </TableCell>
    )
  }

  const asOfDate = actual.asOfDate ?? actual.periodEndDate
  const formattedAsOfDate = formatAsOfDate(asOfDate)
  const isNegative = actual.value < 0
  const content = (
    <div className="pointer-events-none flex min-h-[88px] w-full items-center px-4 py-4 text-sm">
      {formattedAsOfDate ? (
        <div
          className="absolute right-0 top-0 size-3 rounded-[1px] bg-primary/30 transition-colors group-hover:bg-primary/80"
          style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }}
          aria-hidden="true"
        />
      ) : null}
      <span
        className={cn(
          'font-medium tabular-nums text-primary',
          isNegative && 'text-destructive'
        )}
      >
        {formatValue(actual.value, dataType, unitLabel)}
      </span>
    </div>
  )

  if (!formattedAsOfDate) {
    return (
      <TableCell className="relative cursor-pointer p-0">{content}</TableCell>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <TableCell
          className="group relative cursor-help p-0"
          aria-haspopup="dialog"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {content}
        </TableCell>
      </PopoverAnchor>
      <PopoverContent
        className="w-auto rounded-md border border-primary/20 bg-white px-3 py-2 shadow-lg"
        side="top"
        align="center"
        sideOffset={6}
      >
        <Typography
          variant="body-sm"
          className="whitespace-nowrap text-xs font-medium text-primary"
        >
          As of · {formattedAsOfDate}
        </Typography>
      </PopoverContent>
    </Popover>
  )
}

function BulletBar({
  kpi,
  goalProgress,
  className,
}: {
  kpi: ScorecardKpiDto
  goalProgress: ScorecardGoalProgress | null
  className?: string
}) {
  const progressPercent = goalProgress?.percent ?? kpi.progress?.percent ?? null
  const targetX = 72
  const fillWidth =
    progressPercent === null
      ? 0
      : Math.max(0, Math.min(96, (progressPercent / 100) * targetX))
  const fillClass =
    kpi.status === 'AT_RISK'
      ? 'bg-amber-600'
      : kpi.status === 'OFF_TRACK'
        ? 'bg-destructive'
        : kpi.status === 'ACHIEVED'
          ? 'bg-emerald-700'
          : 'bg-[#9bbf6b]'

  return (
    <div className={cn('relative h-5 w-24', className)} aria-hidden="true">
      <div className="absolute left-0 top-1/2 h-2 w-full -translate-y-1/2 rounded-full bg-primary/6" />
      <div
        className={cn(
          'absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full',
          fillClass
        )}
        style={{ width: `${fillWidth}%` }}
      />
      <div
        className="absolute top-1/2 h-5 w-px -translate-y-1/2 bg-primary"
        style={{ left: `${targetX}%` }}
      />
      <div
        className="absolute top-1 h-0 w-0 border-x-[4px] border-t-[5px] border-x-transparent border-t-primary"
        style={{ left: `calc(${targetX}% - 4px)` }}
      />
    </div>
  )
}

function TrendSparkline({ data }: { data: ScorecardChartDatum[] }) {
  const values = data
    .map((point, index) => ({ value: point.actual, index }))
    .filter(
      (point): point is { value: number; index: number } => point.value !== null
    )

  if (values.length === 0) {
    return <span className="text-primary/70">No trend yet</span>
  }

  const width = 104
  const height = 30
  const padding = 3
  const min = Math.min(...values.map((point) => point.value))
  const max = Math.max(...values.map((point) => point.value))
  const span = max - min || 1
  const maxIndex = Math.max(data.length - 1, 1)
  const points = values.map((point) => {
    const x = padding + (point.index / maxIndex) * (width - padding * 2)
    const y =
      height - padding - ((point.value - min) / span) * (height - padding * 2)
    return [x, y] as const
  })
  const path = points
    .map(
      ([x, y], index) =>
        `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`
    )
    .join(' ')
  const last = points[points.length - 1]

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-8 w-24">
      <path
        d={path}
        fill="none"
        stroke="#7aa0c2"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {last ? <circle cx={last[0]} cy={last[1]} r="2.6" fill={NAVY} /> : null}
    </svg>
  )
}

function TargetPathCheckpointDot({
  cx,
  cy,
  payload,
}: {
  cx?: number
  cy?: number
  payload?: ScorecardChartDatum
}) {
  if (!payload?.isCheckpoint || cx === undefined || cy === undefined) {
    return null
  }

  return (
    <g aria-label={payload.checkpointLabel ?? 'Checkpoint'}>
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill="white"
        stroke={CORAL}
        strokeWidth={2}
      />
      <circle cx={cx} cy={cy} r={2.5} fill={CORAL} />
    </g>
  )
}

function ScorecardChartTooltip({
  active,
  payload,
  label,
  dataType,
  unitLabel,
  labelByDate,
}: {
  active?: boolean
  payload?: {
    dataKey?: string | number
    value?: number | null
    color?: string
    payload?: ScorecardChartDatum
  }[]
  label?: string
  dataType: ScorecardMetricDataType | undefined
  unitLabel?: string | null
  labelByDate: Map<string, string>
}) {
  if (!active || !payload?.length) return null

  const labelText = label ? (labelByDate.get(label) ?? label) : ''
  const checkpointLabel =
    payload.find((item) => item.payload?.isCheckpoint)?.payload
      ?.checkpointLabel ?? null

  return (
    <ChartTooltipContent className="min-w-44 bg-white">
      <div className="mb-1 font-medium text-primary">{labelText}</div>
      {checkpointLabel ? (
        <div className="mb-2 text-xs text-[#c0654a]">
          Checkpoint: {checkpointLabel}
        </div>
      ) : null}
      <div className="space-y-1">
        {payload.map((item) => (
          <div
            key={String(item.dataKey)}
            className="flex items-center justify-between gap-3"
          >
            <span className="flex items-center gap-1.5 text-primary/70">
              <span
                className="h-2 w-2 rounded-sm"
                style={{ backgroundColor: item.color }}
              />
              {item.dataKey === 'target' ? 'Goal path' : 'Actual'}
            </span>
            <span
              className={cn(
                'font-medium tabular-nums text-primary',
                (item.value ?? 0) < 0 && 'text-destructive'
              )}
            >
              {formatValue(item.value ?? null, dataType, unitLabel)}
            </span>
          </div>
        ))}
      </div>
    </ChartTooltipContent>
  )
}

function ScorecardDetail({
  kpi,
  chartData,
  chartRange,
  isLoading,
  isError,
  dataType,
  unitLabel,
}: {
  kpi: ScorecardKpiDto
  chartData: ScorecardChartDatum[]
  chartRange: ScorecardChartRange
  isLoading: boolean
  isError: boolean
  dataType: ScorecardMetricDataType | undefined
  unitLabel?: string | null
}) {
  const hasData = chartData.some((point) => point.actual !== null)
  const hasCheckpoints = chartData.some((point) => point.isCheckpoint)
  const visibleAxisTickCount = chartData.filter(
    (point) => point.axisLabel !== ''
  ).length
  const axisLabelByDate = useMemo(
    () =>
      new Map(
        chartData.map((point) => [
          point.periodEndDate,
          point.axisLabel || point.label,
        ])
      ),
    [chartData]
  )

  return (
    <div className="space-y-3 bg-[#f8fafc] px-5 py-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0">
          <Typography variant="body-sm" className="font-medium text-primary">
            {getKpiName(kpi)} — actual vs. goal path
          </Typography>
          <Typography
            variant="body-sm"
            className="mt-0.5 text-xs text-primary/70"
          >
            {chartRange === 'GOAL_PERIOD'
              ? `Goal window: ${formatGoalWindow(kpi)}`
              : `Full history, with goal path highlighted from ${formatGoalWindow(kpi)}`}
            {' · '}
            {formatCheckpointSummary(kpi)}
          </Typography>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-primary/70">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" />{' '}
            Actual
            <span className="inline-block h-0.5 w-5 rounded-full bg-[#c0654a]" />{' '}
            Goal path
            {hasCheckpoints ? (
              <>
                <span className="inline-block size-2.5 rounded-full border-2 border-[#c0654a] bg-white" />{' '}
                Checkpoint
              </>
            ) : null}
          </div>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full rounded-sm" />
      ) : isError ? (
        <div className="flex h-56 items-center justify-center rounded-sm border border-primary/10 bg-white text-primary/70">
          Unable to load trend data.
        </div>
      ) : !hasData ? (
        <div className="flex h-56 items-center justify-center rounded-sm border border-primary/10 bg-white text-primary/70">
          No Chronograph trend data is available for this KPI yet.
        </div>
      ) : (
        <ChartContainer
          config={chartConfig}
          className="h-64 w-full aspect-auto rounded-sm bg-white p-3 [&_.recharts-tooltip-cursor]:fill-transparent"
        >
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
            barCategoryGap={chartData.length > 12 ? '10%' : '20%'}
          >
            <CartesianGrid vertical={false} stroke={GRID_STROKE} />
            <XAxis
              dataKey="periodEndDate"
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              interval={visibleAxisTickCount > 12 ? 1 : 0}
              tickFormatter={(value) =>
                axisLabelByDate.get(String(value)) ?? ''
              }
              tick={{
                fill: 'color-mix(in srgb, var(--primary) 55%, transparent)',
                fontSize: 10,
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                fill: 'color-mix(in srgb, var(--primary) 55%, transparent)',
                fontSize: 10,
              }}
              tickFormatter={(value: number) =>
                formatValue(value, dataType, unitLabel)
              }
              width={64}
            />
            <ChartTooltip
              content={
                <ScorecardChartTooltip
                  dataType={dataType}
                  unitLabel={unitLabel}
                  labelByDate={axisLabelByDate}
                />
              }
            />
            <Bar dataKey="actual" maxBarSize={56} radius={[4, 4, 0, 0]}>
              {chartData.map((point, index) => (
                <Cell
                  key={`${point.label}-${index}`}
                  fill={
                    point.actual != null && point.actual < 0 ? '#c0392b' : NAVY
                  }
                />
              ))}
            </Bar>
            <Line
              type="monotone"
              dataKey="target"
              stroke={CORAL}
              strokeWidth={2.5}
              strokeDasharray="6 4"
              dot={<TargetPathCheckpointDot />}
              activeDot={{ r: 4 }}
              connectNulls
            />
          </ComposedChart>
        </ChartContainer>
      )}

      <div className="flex items-start gap-2 text-xs leading-5 text-primary/70">
        <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full border border-[#c0654a]" />
        <span>
          Goal path shows the planned pace from the starting value to{' '}
          {formatScorecardGoal(kpi, dataType)} by {formatDate(kpi.targetDate)}.
          Checkpoints are planned waypoints, not actuals.
          {hasCheckpoints ? ' Ring markers show checkpoints on the path.' : ''}
        </span>
      </div>
    </div>
  )
}

function ScorecardKpiRows({
  kpi,
  isOpen,
  canEdit,
  surface,
  companyId,
  fiscalYearEnd,
  onToggle,
  onEdit,
  onRecordActual,
  onDeleteTarget,
  onStatusChange,
  isStatusUpdating,
  ogsmLink,
}: {
  kpi: ScorecardKpiDto
  isOpen: boolean
  canEdit: boolean
  surface: ScorecardSurface
  companyId?: string
  fiscalYearEnd?: string | null
  onToggle: () => void
  onEdit?: (kpi: ScorecardKpiDto) => void
  onRecordActual?: (kpi: ScorecardKpiDto) => void
  onDeleteTarget: (kpi: ScorecardKpiDto) => void
  onStatusChange?: (
    kpi: ScorecardKpiDto,
    status: ScorecardKpiStatus | 'CALCULATED'
  ) => Promise<void>
  isStatusUpdating: boolean
  ogsmLink: boolean
}) {
  const comparisonPeriod = scorecardPeriodToChartPeriod(kpi.periodType)
  const initialChartView = scorecardPeriodToInitialChartView(kpi.periodType)
  const [chartViewState, setChartViewState] = useState<{
    periodType: ScorecardPeriodType
    view: CompanyMetricsView
  }>(() => ({
    periodType: kpi.periodType,
    view: initialChartView,
  }))
  const [chartRange, setChartRange] =
    useState<ScorecardChartRange>('GOAL_PERIOD')
  const chartViewOptions = getChartViewOptions(kpi)
  const requestedChartView =
    chartViewState.periodType === kpi.periodType
      ? chartViewState.view
      : initialChartView
  const chartView = chartViewOptions.some(
    (option) => option.value === requestedChartView
  )
    ? requestedChartView
    : initialChartView
  const {
    series: comparisonSeries,
    isLoading: isComparisonLoading,
    isFetching: isComparisonFetching,
  } = useScorecardSeries({
    surface,
    companyId,
    metricLabelId: kpi.metricLabelId,
    periodType: comparisonPeriod,
    enabled: true,
  })
  const {
    series: chartSeries,
    isLoading: isChartLoading,
    isFetching: isChartFetching,
    isError: isChartError,
  } = useScorecardSeries({
    surface,
    companyId,
    metricLabelId: kpi.metricLabelId,
    periodType: comparisonPeriod,
    view: chartView,
    enabled: isOpen,
  })
  const dataType = getKpiDataType(kpi)
  const goalPercentage = formatScorecardGoalPercentage(kpi)
  const goalChangeLabel = getScorecardGoalChangeLabel(kpi)
  const GoalDirectionIcon = goalChangeLabel === 'decrease' ? ArrowDown : ArrowUp
  const source =
    kpi.actualSource === 'MANUAL' || kpi.actualEditable
      ? 'Manual'
      : 'Chronograph'
  const direction = getScorecardDirectionLabel(kpi)
  const trendData = useMemo(
    () =>
      buildScorecardChartData(kpi, comparisonSeries?.points ?? [], {
        appendTargetPoint: false,
        range: 'FULL_HISTORY',
      }),
    [comparisonSeries?.points, kpi]
  )
  const chartData = useMemo(
    () =>
      buildScorecardChartData(kpi, chartSeries?.points ?? [], {
        range: chartRange,
        view: chartView,
        fiscalYearEnd,
      }),
    [chartRange, chartSeries?.points, chartView, fiscalYearEnd, kpi]
  )
  const goalProgress = useMemo<ScorecardGoalProgress | null>(() => {
    if (!kpi.progress || kpi.progress.percent === null) return null
    return { ...kpi.progress, percent: kpi.progress.percent }
  }, [kpi.progress])
  const startValue = useMemo(
    () => buildScorecardStartValue(kpi, comparisonSeries?.points ?? []),
    [comparisonSeries?.points, kpi]
  )
  const isComparisonSeriesLoading = isComparisonLoading || isComparisonFetching
  const colSpan = canEdit ? 8 : 7

  return (
    <Fragment>
      <TableRow
        data-state={isOpen ? 'selected' : undefined}
        className={cn(
          'cursor-pointer bg-white hover:bg-primary/5 data-[state=selected]:bg-[#fbefe9] [&>td]:cursor-pointer',
          isOpen && 'border-b-0'
        )}
        onClick={onToggle}
      >
        <TableCell className="w-[220px] py-4">
          <div className="flex min-w-0 items-center gap-3">
            <ChevronRight
              className={cn(
                'size-3.5 shrink-0 text-primary/70 transition-transform',
                isOpen && 'rotate-90 text-[#c0654a]'
              )}
            />
            <div className="min-w-0">
              <Typography
                variant="body-sm"
                className="truncate text-sm font-medium text-primary"
              >
                {getKpiName(kpi)}
              </Typography>
              <Typography
                variant="body-sm"
                className="truncate text-xs font-medium text-primary/70"
              >
                {[source, direction, formatPeriodLabel(kpi.periodType)]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              {ogsmLink && surface === 'internal' && kpi.ogsmNodeId ? (
                <Button
                  asChild
                  variant="link"
                  size="sm"
                  className="mt-1 h-auto gap-1 p-0 text-xs [&_svg]:size-3"
                >
                  <Link
                    to="/companies/$companyId/ogsm"
                    params={{ companyId: kpi.companyId }}
                    search={{ objective: kpi.ogsmNodeId }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    Change in OGSM
                    <ArrowUpRight aria-hidden="true" />
                  </Link>
                </Button>
              ) : null}
            </div>
          </div>
        </TableCell>
        <StartValueCell
          startValue={startValue}
          dataType={dataType}
          unitLabel={kpi.unitLabel}
          isLoading={kpi.startValue === null && isComparisonSeriesLoading}
        />
        <ActualValueCell
          kpi={kpi}
          dataType={dataType}
          unitLabel={kpi.unitLabel}
        />
        <TableCell className="py-4 whitespace-normal">
          <Typography
            variant="body-sm"
            className="text-sm font-medium tabular-nums text-primary"
          >
            {formatValue(kpi.targetValue, dataType, kpi.unitLabel)}
          </Typography>
          <div className="mt-1 flex flex-col gap-0.5">
            {goalPercentage !== null ? (
              <Typography
                as="span"
                variant="body-sm"
                className="inline-flex items-center gap-1 text-[11px] text-primary/70 whitespace-nowrap"
              >
                {goalChangeLabel !== 'no change' && (
                  <GoalDirectionIcon
                    size={14}
                    strokeWidth={1.5}
                    className="shrink-0"
                    aria-hidden="true"
                  />
                )}
                {goalPercentage}{' '}
                <span className="sr-only">{goalChangeLabel}</span>
              </Typography>
            ) : null}
            <Typography
              variant="body-sm"
              className="text-[11px] text-primary/70 whitespace-nowrap"
            >
              {formatTargetDueLabel(kpi, fiscalYearEnd)}
            </Typography>
          </div>
        </TableCell>
        <GoalProgressCell
          kpi={kpi}
          progress={goalProgress}
          dataType={dataType}
          unitLabel={kpi.unitLabel}
          isLoading={false}
        />
        <TableCell className="py-4">
          {isComparisonSeriesLoading ? (
            <Skeleton className="h-7 w-24" />
          ) : (
            <TrendSparkline data={trendData} />
          )}
        </TableCell>
        <StatusCell
          kpi={kpi}
          canEdit={canEdit}
          isUpdating={isStatusUpdating}
          onStatusChange={onStatusChange}
        />
        {canEdit ? (
          <TableCell
            className="cursor-default py-4"
            onClick={(event) => event.stopPropagation()}
          >
            <RowActionsMenu label={getKpiName(kpi)}>
              {kpi.actualEditable ? (
                <DropdownMenuItem onClick={() => onRecordActual?.(kpi)}>
                  <Plus className="size-4" />
                  Record actual
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                disabled={kpi.configurationEditable === false}
                onClick={() => onEdit?.(kpi)}
              >
                <Pencil className="size-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={kpi.configurationEditable === false}
                onClick={() => onDeleteTarget(kpi)}
                className="text-destructive hover:text-destructive focus:text-destructive"
              >
                <Trash2 className="size-4" />
                Remove
              </DropdownMenuItem>
            </RowActionsMenu>
          </TableCell>
        ) : null}
      </TableRow>
      {isOpen ? (
        <TableRow className="bg-[#f8fafc] hover:bg-[#f8fafc]">
          <TableCell
            colSpan={colSpan}
            className="border-r-0 p-0 whitespace-normal"
          >
            <div className="border-t border-primary/10">
              <div className="flex flex-wrap items-center justify-end gap-2 bg-[#f8fafc] px-5 pt-4">
                <Typography variant="overline" className="text-primary/70">
                  Range
                </Typography>
                <div className="inline-flex rounded-md border border-primary/10 bg-white p-1">
                  {CHART_RANGE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={cn(
                        'cursor-pointer rounded px-3 py-1 text-xs text-primary/70 transition-colors hover:bg-primary/5',
                        chartRange === option.value &&
                          'bg-primary text-primary-foreground hover:bg-primary'
                      )}
                      aria-pressed={chartRange === option.value}
                      onClick={(event) => {
                        event.stopPropagation()
                        setChartRange(option.value)
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <Typography variant="overline" className="text-primary/70">
                  Show chart as
                </Typography>
                <div className="inline-flex rounded-md border border-primary/10 bg-white p-1">
                  {chartViewOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={cn(
                        'cursor-pointer rounded px-3 py-1 text-xs text-primary/70 transition-colors hover:bg-primary/5',
                        chartView === option.value &&
                          'bg-primary text-primary-foreground hover:bg-primary'
                      )}
                      aria-pressed={chartView === option.value}
                      onClick={(event) => {
                        event.stopPropagation()
                        setChartViewState({
                          periodType: kpi.periodType,
                          view: option.value,
                        })
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <span className="rounded-full border border-primary/10 bg-white px-2 py-1 text-[11px] text-primary/70">
                  Goal measured {formatPeriodLabel(kpi.periodType)}
                </span>
              </div>
              <ScorecardDetail
                kpi={kpi}
                chartData={chartData}
                chartRange={chartRange}
                isLoading={isChartLoading || isChartFetching}
                isError={isChartError}
                dataType={dataType}
                unitLabel={kpi.unitLabel}
              />
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </Fragment>
  )
}

function RowActionsMenu({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-primary/70 hover:text-primary"
          aria-label={`Open actions for ${label}`}
        >
          <EllipsisVertical className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ScorecardSectionRow({
  title,
  description,
  colSpan,
}: {
  title: string
  description?: string
  colSpan: number
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell
        colSpan={colSpan}
        className="bg-[#f8fafc] px-4 py-3 text-primary"
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm font-medium text-primary">{title}</span>
          {description ? (
            <span className="text-xs text-primary/70">{description}</span>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  )
}

function ScorecardTrackedKpiRow({
  kpi,
  canEdit,
  surface,
  companyId,
  onAddTarget,
}: {
  kpi: ScorecardTrackedKpiDto
  canEdit: boolean
  surface: ScorecardSurface
  companyId?: string
  onAddTarget?: (kpi: ScorecardTrackedKpiDto) => void
}) {
  const trendPeriod = getTrackedKpiTrendPeriod(kpi.availablePeriods)
  const {
    series: comparisonSeries,
    isLoading: isComparisonLoading,
    isFetching: isComparisonFetching,
  } = useScorecardSeries({
    surface,
    companyId,
    metricLabelId: kpi.metricLabelId,
    periodType: trendPeriod ? scorecardPeriodToChartPeriod(trendPeriod) : 'LTM',
    enabled: Boolean(trendPeriod),
  })
  const dataType = getTrackedKpiDataType(kpi)
  const format = formatForDataType(dataType)
  const source = kpi.metricLabel.globalSectionName
  const trendData = useMemo(
    () => buildTrackedSparklineData(comparisonSeries?.points ?? []),
    [comparisonSeries?.points]
  )

  return (
    <TableRow className="bg-white hover:bg-primary/3">
      <TableCell className="w-[220px] py-4">
        <div className="min-w-0 pl-6">
          <Typography
            variant="body-sm"
            className="truncate text-sm font-medium text-primary"
          >
            {getTrackedKpiName(kpi)}
          </Typography>
          <Typography
            variant="body-sm"
            className="truncate text-xs font-medium text-primary/70"
          >
            {[source, trendPeriod ? formatPeriodLabel(trendPeriod) : null]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </div>
      </TableCell>
      <TableCell className="py-4">
        <span className="text-sm text-primary/70">—</span>
      </TableCell>
      <TableCell className="py-4">
        {kpi.actual ? (
          <>
            <CellNumber
              value={kpi.actual.value}
              format={format}
              className="text-sm font-medium"
            />
            <div className="mt-1 text-[11px] text-primary/70">
              {formatAsOfDate(
                kpi.actual.asOfDate ?? kpi.actual.periodEndDate
              ) ?? '—'}
            </div>
          </>
        ) : (
          <span className="text-sm text-primary/70">—</span>
        )}
      </TableCell>
      <TableCell className="py-4">
        <span className="text-sm text-primary/70">—</span>
      </TableCell>
      <TableCell className="py-4">
        <span className="text-sm text-primary/70">—</span>
      </TableCell>
      <TableCell className="py-4">
        {isComparisonLoading || isComparisonFetching ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <TrendSparkline data={trendData} />
        )}
      </TableCell>
      <TableCell className="py-4">
        <span className="text-sm text-primary/70">—</span>
      </TableCell>
      {canEdit ? (
        <TableCell className="cursor-default py-4">
          <RowActionsMenu label={getTrackedKpiName(kpi)}>
            <DropdownMenuItem onClick={() => onAddTarget?.(kpi)}>
              <Plus className="size-4" />
              Add goal
            </DropdownMenuItem>
          </RowActionsMenu>
        </TableCell>
      ) : null}
    </TableRow>
  )
}

interface ScorecardTableProps {
  kpis: ScorecardKpiDto[]
  trackedKpis: ScorecardTrackedKpiDto[]
  canEdit?: boolean
  surface?: ScorecardSurface
  companyId?: string
  fiscalYearEnd?: string | null
  onEdit?: (kpi: ScorecardKpiDto) => void
  onRecordActual?: (kpi: ScorecardKpiDto) => void
  onDelete?: (kpiId: string) => Promise<void>
  onAddTarget?: (kpi: ScorecardTrackedKpiDto) => void
  onStatusChange?: (
    kpi: ScorecardKpiDto,
    status: ScorecardKpiStatus | 'CALCULATED'
  ) => Promise<void>
  isDeleting?: boolean
  isStatusUpdating?: boolean
  /** Links OGSM-published rows back to their objective; needs a router. */
  ogsmLinks?: boolean
}

export function ScorecardTable({
  kpis,
  trackedKpis,
  canEdit = false,
  surface = 'internal',
  companyId,
  fiscalYearEnd,
  onEdit,
  onRecordActual,
  onDelete,
  onAddTarget,
  onStatusChange,
  isDeleting = false,
  isStatusUpdating = false,
  ogsmLinks = false,
}: ScorecardTableProps) {
  const [deleteTarget, setDeleteTarget] = useState<ScorecardKpiDto | null>(null)
  const [openKpiId, setOpenKpiId] = useState<string | null>(null)
  const colSpan = canEdit ? 8 : 7
  const tableMinWidth = canEdit
    ? SCORECARD_TABLE_EDIT_MIN_WIDTH
    : SCORECARD_TABLE_MIN_WIDTH

  const handleDelete = async () => {
    if (!deleteTarget || !onDelete) return
    try {
      await onDelete(deleteTarget.id)
      setDeleteTarget(null)
    } catch {
      // Axios interceptor shows the error toast. Keep the dialog open so the
      // user can retry or cancel.
    }
  }

  return (
    <>
      <div className="max-w-full overflow-hidden rounded-lg border border-primary/10 bg-white">
        <div
          role="region"
          aria-label="Scorecard metrics"
          tabIndex={0}
          className="max-w-full overflow-x-auto overflow-y-visible scrollbar-visible"
        >
          <Table
            disableWrapper
            className="table-fixed [&_th]:border-primary/12 [&_td]:border-primary/10 [&_th:first-child]:border-l-0 [&_td:first-child]:border-l-0 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0 [&_tbody_tr:last-child_td]:border-b-0"
            style={{ minWidth: tableMinWidth }}
          >
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="w-[220px] text-sm font-normal text-primary/70"
                >
                  Objective
                </TableHead>
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="w-[128px] text-sm font-normal text-primary/70"
                >
                  Starting value
                </TableHead>
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="w-[128px] text-sm font-normal text-primary/70"
                >
                  Actual
                </TableHead>
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="w-[136px] text-sm font-normal text-primary/70"
                >
                  Goal
                </TableHead>
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="w-[172px] text-sm font-normal text-primary/70"
                >
                  Progress
                </TableHead>
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="w-[116px] text-sm font-normal text-primary/70"
                >
                  Trend
                </TableHead>
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="w-[104px] text-sm font-normal text-primary/70"
                >
                  Status
                </TableHead>
                {canEdit ? (
                  <TableHead
                    style={{ background: HEADER_BG }}
                    className="w-[68px]"
                  />
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {kpis.length > 0 ? (
                kpis.map((kpi) => (
                  <ScorecardKpiRows
                    key={kpi.id}
                    kpi={kpi}
                    isOpen={openKpiId === kpi.id}
                    canEdit={canEdit}
                    surface={surface}
                    companyId={companyId}
                    fiscalYearEnd={fiscalYearEnd}
                    onToggle={() =>
                      setOpenKpiId((current) =>
                        current === kpi.id ? null : kpi.id
                      )
                    }
                    onEdit={onEdit}
                    onRecordActual={onRecordActual}
                    onDeleteTarget={setDeleteTarget}
                    onStatusChange={onStatusChange}
                    isStatusUpdating={isStatusUpdating}
                    ogsmLink={ogsmLinks}
                  />
                ))
              ) : (
                <TableRow className="bg-white hover:bg-white">
                  <TableCell
                    colSpan={colSpan}
                    className="px-4 py-4 text-sm text-primary/70"
                  >
                    No scorecard goals yet.
                  </TableCell>
                </TableRow>
              )}
              {trackedKpis.length > 0 ? (
                <>
                  <ScorecardSectionRow
                    title="Tracked metrics"
                    description="Metrics without goals yet"
                    colSpan={colSpan}
                  />
                  {trackedKpis.map((kpi) => (
                    <ScorecardTrackedKpiRow
                      key={kpi.id}
                      kpi={kpi}
                      canEdit={canEdit}
                      surface={surface}
                      companyId={companyId}
                      onAddTarget={onAddTarget}
                    />
                  ))}
                </>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </div>

      <ContactDeleteDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        title="Remove objective from scorecard?"
        description={
          deleteTarget ? (
            <>
              Remove{' '}
              <span className="font-semibold text-primary">
                {getKpiName(deleteTarget)}
              </span>{' '}
              from this scorecard? The underlying Chronograph metric stays
              intact.
            </>
          ) : null
        }
        confirmLabel="Remove"
        isPending={isDeleting}
        onConfirm={handleDelete}
      />
    </>
  )
}
